/**
 * Admin Metrics Tab Component
 * 
 * Displays detailed metrics including entry gate metrics, queue metrics,
 * reconciliation, and orphan linking statistics.
 */

import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { getOpsMetrics, type OpsMetricsResponse } from "@app/services/adminOpsMetricsApi";
import "./admin-metrics.css";

const METRICS_QUERY_KEY = ["admin", "metrics"];

export function AdminMetricsTab(): JSX.Element {
  const { data: metrics, isLoading, isError } = useQuery<OpsMetricsResponse>({
    queryKey: METRICS_QUERY_KEY,
    queryFn: getOpsMetrics,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="admin-metrics-root">
        <Card title="Metrics">
          <div className="admin-metrics-loading">Loading metrics data...</div>
        </Card>
      </div>
    );
  }

  if (isError || !metrics) {
    return (
      <div className="admin-metrics-root">
        <Card title="Metrics">
          <div className="admin-metrics-error">Failed to load metrics data.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-metrics-root">
      <Card
        title="Metrics"
        subtitle={`Last updated: ${new Date(metrics.timestamp).toLocaleString()}`}
      >
        <div className="admin-metrics-grid">
          {/* Queue Metrics */}
          <div className="admin-metrics-section">
            <h3>Queue Metrics</h3>
            <div className="admin-metrics-table">
              <table>
                <thead>
                  <tr>
                    <th>Stream</th>
                    <th>Length</th>
                    <th>Pending</th>
                    <th>Consumer Lag</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metrics.queue_metrics).map(([streamType, streamMetrics]) => (
                    <tr key={streamType}>
                      <td>{streamMetrics.stream_type}</td>
                      <td>{streamMetrics.stream_length}</td>
                      <td className={streamMetrics.pending_count > 0 ? "warning" : ""}>
                        {streamMetrics.pending_count}
                      </td>
                      <td>{streamMetrics.consumer_lag}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Entry Gate Metrics */}
          <div className="admin-metrics-section">
            <h3>Entry Gate Metrics</h3>
            <div className="admin-metrics-cards">
              {Object.entries(metrics.entry_gate_metrics).map(([key, value]) => (
                <div key={key} className="admin-metrics-card">
                  <div className="admin-metrics-card__label">{key}</div>
                  <div className="admin-metrics-card__value">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reconciliation & Orphan Metrics */}
          <div className="admin-metrics-section">
            <h3>Reconciliation & Orphan Metrics</h3>
            <div className="admin-metrics-cards">
              <div className="admin-metrics-card">
                <div className="admin-metrics-card__label">Reconciliation Corrections</div>
                <div className="admin-metrics-card__value">
                  {metrics.reconciliation_metrics.corrections_total}
                </div>
              </div>
              <div className="admin-metrics-card">
                <div className="admin-metrics-card__label">Orphan Links Created</div>
                <div className="admin-metrics-card__value">
                  {metrics.orphan_metrics.links_created_total}
                </div>
              </div>
              <div className="admin-metrics-card">
                <div className="admin-metrics-card__label">Orphan Links Failed</div>
                <div className="admin-metrics-card__value">
                  {metrics.orphan_metrics.links_failed_total}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

